/**
 * ダウンロード関連のClass設定や定義など
 */

import camelcaseKeys from 'camelcase-keys'
import { SectionItem } from '../class/SectionList'

/**
 * DL処理ステータス
 */
export enum DownloadStatus {
  Unauthorized = 0, // 認証前
  DownloadOK = 1, // DL,OK
  UnderLevels = 2, // 権限不足
  NotLoggedIn = 3, // 未ログイン
  ReDownloadTry = 4, // 再ダウンロードトライ
  ReDownloadWait = 5 // 再ダウンロード待ち
}

/**
 * ファイルサイズ表示用のフォーマッタ
 */
export class FileSizeFormatter {
  // byte→を変換（小数点第２位まで保持）
  public static fileSizeFormat(size: string): string {
    let bytes = Number(size)

    let precision = 2
    if (Math.abs(bytes) < 1024) {
      precision = 0
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB']

    let sign = ''
    if (bytes < 0) {
      sign = '-'
      bytes = Math.abs(bytes)
    }

    const point = function (num: number, digit: number) {
      const time = Math.pow(10, digit)
      return Math.floor(num * time) / time
    }

    const exp = Math.floor(Math.log(bytes) / Math.log(1024))
    const unit = units[exp]

    bytes = bytes / Math.pow(1024, Math.floor(exp))
    bytes = point(bytes, precision)

    return sign + bytes + unit
  }
}


/**
 * ファイル情報の基底クラス
 */
export default class DownloadBase {
  public title = ''
  public contentType = '' // DL履歴登録で必要なためここで定義
  public contentTypeCode = ''
  public fileType = ''
  public fileSize = ''
  public fileSizeOrg = 0
  public url = ''
  public region: SectionItem[] = []

  constructor(init: Record<string, string> = {}) {
    this.jsonToObject(init)
  }

  public jsonToObject(data: Record<string, unknown>): void {
    Object.assign(this, camelcaseKeys(data, { deep: true }))

    // ファイルサイズ計算
    this.fileSizeOrg = Number(this.fileSize)
    this.fileSize = FileSizeFormatter.fileSizeFormat(this.fileSize)

    // ファイルタイプセット
    if (data.type) {
       this.fileType = data.type as string
    }
  }

  public setContentType(): void {
    // ドキュメントタイプを取得
    const documentInfo: SectionItem | undefined = this.region.find(
      (item: SectionItem) =>
        item.type === 'document'
    )

    if (documentInfo) {
      this.contentTypeCode = documentInfo.subcategoryCode
      this.contentType = documentInfo.subcategoryName
    }
  }
}

/**
 * DL履歴APIから渡されたファイル情報のクラス
 */
export class DownloadHistoryInfo extends DownloadBase {
  public date = ''

  constructor(init: Record<string, string> = {}) {
    super()
    this.jsonToObject(init)
  }
}

/**
 * リンク用JSONから渡されたファイル情報のクラス
 */
export class DownloadLinkInfo extends DownloadBase {
  public downloadLevel = 0
  public id = 0
  public entityId = 0
  public key = ''
  public siteCode = ''
  public visibleCountry: string[] = []
  public invisibleCountry: string[] = []
  public disabledSites: string[] = []
  public healthCare = false
  public createDate = 0

  /**
   * @returns PDFの作成日時のフォーマット済み文字列
   */
  public getCreateDateString(): string {
    // TODO:フォーマットを設定から取得するようにする
    // console.log(this)
    if (this.createDate < 1000) {
      // 日付が未指定
      return ''
    }
    const date = new Date(this.createDate * 1000)

    const year = date.getFullYear().toString()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const formatted = year + '-' + month + '-' + day

    return formatted
  }

  /**
   * 現在のサイトのデータかを判定
   * @returns 可否
   */
  public isCurrentSite(): boolean {
    const site = drupalSettings.vueComponent.sites.current
    return site.code === this.siteCode
  }

  /**
   * ページのリンク情報を返却する
   *
   * 自サイトの場合、相対パス。他サイトの場合、絶対パスで返却する
   * @returns
   */
  public getHref(): string {
    try {
      const url = new URL(this.url)
      return this.isCurrentSite() ? url.pathname : url.toString()
    } catch {
      return this.url
    }
  }

  constructor(init: Record<string, unknown>) {
    super()
    this.jsonToObject(init)
  }
}


/**
 * PDFのソート
 *
 * 日付降順 > id降順 で並び替える
 */
export function sortPdf(a: DownloadLinkInfo, b: DownloadLinkInfo): number {
  // 日付の降順
  if (a.createDate !== b.createDate) {
    return a.createDate < b.createDate ? 1 : -1
  }

  // fidの降順
  if (a.id !== b.id) {
    return a.id < b.id ? 1 : -1
  }

  return 0
}
